import { HelpTopic } from './helpContent';
import { zonesHelpTopics } from './zonesHelpContent';

/**
 * Help content for the Auto Correlation tab on the Correlation page.
 * Explains the assisted workflow (Scope → Run → Review → Apply), how each
 * candidate fit is scored (statistics AND physics, not R² alone), why the
 * conventional-form prior is only a tie-breaker, and how the recommended
 * combination is validated through the Kr × Pc model.
 *
 * The scoring copy mirrors the real algorithm in
 * `backend/.../use_cases/auto_correlation/scoring.py` (scoring_version v2)
 * and `curve_validation.py`; keep them in sync if the algorithm changes.
 */
export const autoCorrelationHelpTopics: HelpTopic[] = [
  {
    id: 'auto-correlation',
    label: 'Auto Correlation',
    title: 'Auto Correlation — assisted selection',
    intro:
      'Auto Correlation is a first-pass helper that suggests a fit function for every correlated parameter, using the same samples and the same regression engine as the manual workflow. It is an assisted interpretation tool, not a replacement for expert judgement: nothing is written to the project until you review the suggestions and apply. The manual workflow on the other tab stays exactly as it was.',
    sections: [
      {
        id: 'auto-why',
        title: 'What it does and why it exists',
        blocks: [
          {
            type: 'paragraph',
            text: 'The manual workflow asks you to pick samples per family, eyeball four fitted functions per parameter, and choose one each before the Kr × Pc model can be built. Auto Correlation automates that first pass: it fits every candidate, scores each on statistical fit and physical plausibility, simulates the resulting Kr × Pc curves, and hands you a recommendation you can accept, override, or ignore.',
          },
          {
            type: 'bullets',
            items: [
              'It follows specialist interpretation principles — the best mathematical fit is deliberately not always the winner (see the Scoring tab).',
              'It never selects by R² alone: sign, trend direction, magnitude at each rock-type FZI, and the shape of the final curves all count.',
              'It always leaves the decision with you — every parameter has an editable dropdown, and rejected alternatives stay visible.',
            ],
          },
          {
            type: 'note',
            title: 'Manual workflow is untouched',
            text: 'Applying an auto recommendation writes ordinary selected functions — exactly the same fields the manual dropdowns write. You can switch to the Manual Correlation tab afterwards and keep adjusting by hand.',
          },
        ],
      },
      {
        id: 'auto-flow',
        title: 'Scope → Run → Review → Apply',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Scope — choose which samples feed the correlation. Each analysis family (Liao Pc, Brooks-Corey Pc, Water-Oil Kr, Gas-Oil Kr) is resolved independently.',
              'Run — pick the x-axis (FZI or RQI) and the gradient difference, then review the fitted and scored candidates before applying them.',
              'Review — read the per-parameter candidate tables (score, R², flags), compare the Recommended / Best R² / Current combinations with their curve previews, and override any function.',
              'Apply — confirms the accepted selection, recalculates the correlation for the chosen sample scope, and returns you to the Manual tab where the results and “Auto” badges are visible.',
            ],
          },
          {
            type: 'note',
            title: 'Lab values or Refinement values',
            text: 'Choose one Kr source for the entire run. Lab uses the imported Corey values and calculated lab endpoints; Refinement uses the saved true_* values from Kr Analysis. Refinement is available only when every selected sample in an included Kr family has all required calculated fields. The app never fills a missing refined value with a lab value, because that would mix two interpretations in one correlation.',
          },
          {
            type: 'note',
            title: 'Zones are required',
            text: 'Auto Correlation simulates the recommended curves through each flow-unit zone, so the project must have zones first. Without them the tab guides you to create zones on the Manual Correlation tab.',
          },
        ],
      },
      {
        id: 'auto-scope-modes',
        title: 'Sample scope options',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'All project samples',
                description:
                  'Every sample that has the matching analysis performed is included per family.',
                effect:
                  'The broadest scope — a good default for a first pass over the whole project.',
              },
              {
                name: 'Multi Analysis samples',
                description:
                  'The samples from the most recent Multi Analysis batch (used when you arrive from the Multi Analysis “Run Auto Correlation” shortcut).',
                effect:
                  'Bridges the batch analysis straight into a correlation. Falls back to all analysed samples if no batch selection is present.',
              },
              {
                name: 'Current correlation samples',
                description:
                  'The samples already selected in the Manual Correlation tab.',
                effect:
                  'Reproduces your manual selection so you can compare the auto picks against what you would choose.',
              },
              {
                name: 'Manual pick',
                description:
                  'Choose samples individually per family, with a sample-code filter.',
                effect:
                  'Full control — use it to exclude outliers or restrict to a rock-type subset before running.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Fewer than three samples',
            text: 'A family with very few samples is flagged: regressions on two or three points are statistically fragile and the scoring will de-weight them (see the “few samples” penalty in the Scoring tab).',
          },
        ],
      },
    ],
  },
  {
    id: 'auto-scoring',
    label: 'Scoring & selection',
    title: 'How candidates are scored and chosen',
    intro:
      'For each parameter the four function types (linear, power, logarithmic, exponential) are fitted and then judged on statistics AND physics. The highest R² is not an automatic winner — a slightly lower-R² fit wins when it keeps predictions positive, trending the right way, and inside plausible bounds across the rock-type range.',
    sections: [
      {
        id: 'auto-not-r2',
        title: 'Why not just the highest R²',
        blocks: [
          {
            type: 'paragraph',
            text: 'In SCAL interpretation the best regression is not always the best geological answer. A fit can score a high R² and still send a saturation negative at high FZI, or make an entry pressure explode — physically impossible even if statistically tidy. Conversely, when every fit is poor (common for the Liao “b” term, where all four sit around R² ≈ 0.01–0.02) R² carries no signal at all and the shape must decide.',
          },
          {
            type: 'bullets',
            items: [
              'Sign and positivity — saturations, pressures and endpoints must stay positive over the range that matters.',
              'Trend direction — properties must move the physically expected way with rock quality (e.g. Swir, Pce and Pc entry pressure fall as FZI rises).',
              'Magnitude — predicted values must stay within plausible bounds at each zone’s representative FZI, not just where samples happen to sit.',
              'Curve behaviour — the final Kr × Pc curves the selection produces must be usable (see the Validation tab).',
            ],
          },
        ],
      },
      {
        id: 'auto-score-formula',
        title: 'The score',
        blocks: [
          {
            type: 'paragraph',
            text: 'Each candidate that survives the physical hard gates gets a score between 0 and 1, combining three terms and subtracting soft penalties:',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'R² — weight 0.40',
                description:
                  'The statistical goodness of fit, clamped to 0–1.',
                effect:
                  'Rewards fit quality, but never gates: a physically sound low-R² candidate can still win. R² below 0.2 only raises an information flag.',
              },
              {
                name: 'Shape — weight 0.35',
                description:
                  'Whether the predicted trend behaves over the evaluation range, including just outside the sampled data.',
                effect:
                  'Full marks when the trend is right; reduced when the fit leaves physical bounds shortly beyond the observed FZI (the reason a power form is preferred over a linear one for Swir).',
              },
              {
                name: 'Bounds — weight 0.25',
                description:
                  'The fraction of predictions across the evaluation grid that land inside the plausible “soft” range for that parameter.',
                effect:
                  'Rewards a fit whose magnitudes stay realistic at every rock-type FZI, not only inside the sampled span.',
              },
              {
                name: 'Soft penalties',
                description:
                  'Small subtractions for extrapolating past the sampled range, sitting close to a hard bound, or having fewer than five samples.',
                effect:
                  'Nudges the recommendation toward well-supported, comfortably-bounded fits without disqualifying anything.',
              },
            ],
          },
        ],
      },
      {
        id: 'auto-hard-gates',
        title: 'Hard gates — when a candidate is rejected',
        blocks: [
          {
            type: 'paragraph',
            text: 'Before scoring, a candidate is disqualified outright (shown with a red flag and no score) if it fails a physical rule at any rock-type FZI:',
          },
          {
            type: 'bullets',
            items: [
              'The regression could not be fitted (degenerate or non-finite coefficients).',
              'A prediction violates a hard bound at a zone mean — e.g. a saturation outside (0, 1) or a pressure at or below zero.',
              'The trend runs the wrong way for a strictly-directional parameter (e.g. Swir increasing with FZI).',
              'The equation cannot be evaluated over the required range (e.g. a log or power fit at a non-positive x).',
            ],
          },
          {
            type: 'note',
            title: 'Lambda is derived, not selected',
            text: 'The Liao lambda is always computed from the chosen a, b and Pce — it is never picked from its own fit. It is instead checked at the combination stage (it must stay finite and positive per zone).',
          },
        ],
      },
      {
        id: 'auto-tie-break',
        title: 'The conventional-form tie-breaker',
        blocks: [
          {
            type: 'paragraph',
            text: 'Some parameters have a conventional function form — power for Swir, Pce and the Kr endpoints, exponential for the Liao “b” term, linear for residual saturation and Corey oil exponents. This convention is informed by Kozeny-Carman and hydraulic-flow-unit theory, but it is not a guarantee: your data may genuinely prefer a different shape.',
          },
          {
            type: 'bullets',
            items: [
              'The convention is NOT added to the score — data ranks the candidates first.',
              'It only breaks a tie: if the top-scored candidate and the conventional one are statistically comparable (their scores within a small margin), the conventional form is preferred.',
              'When that happens the choice is shown with a “selected by convention” information flag naming both candidates, so you can see exactly when convention — not your data — made the call.',
              'When one fit is clearly better on this dataset, data wins and no tie-break flag appears.',
            ],
          },
          {
            type: 'note',
            title: 'Example of the tie-break rule',
            text: 'For Swir the exponential fit had the higher R² (≈ 0.585) but the power fit (≈ 0.459) was chosen: the difference is not meaningful and power stays positive and monotonic across the whole FZI range. This is exactly the tie-break behaviour — and it is flagged so you know it happened.',
          },
        ],
      },
    ],
  },
  {
    id: 'auto-validation',
    label: 'Validation & review',
    title: 'Validating and reviewing the result',
    intro:
      'A good per-parameter pick can still combine into an unusable curve, so the whole selection is simulated through every zone and checked before it reaches you. The Review step then lets you inspect, compare and override — the recommendation is a starting point, not a verdict.',
    sections: [
      {
        id: 'auto-combination',
        title: 'Combination-level checks',
        blocks: [
          {
            type: 'paragraph',
            text: 'For each candidate combination (Recommended, Best R², and your Current manual selection) the app evaluates every parameter at each zone’s mean FZI, derives the zone values, and generates the full Pc-Sw, height-Sw, Kr-Sw and Kr-Sg curves — then checks that they are physically usable.',
          },
          {
            type: 'bullets',
            items: [
              'Saturation budget — Swir + Sor must leave a mobile window; the gas system must too.',
              'Entry pressures — Pc_max must exceed the entry pressure (Pce for Liao, Pe for Brooks-Corey).',
              'Curve shape — Pc non-increasing and ≥ 0, Krw rising, Krow and Krog falling, Krg rising, all fractions inside [0, 1], height ≥ 0, and endpoints consistent (Krw = 0 at Swir, Krow = 0 at Sw → 1).',
              'Derived lambda — the Liao lambda computed from a, b and Pce must be finite and positive per zone.',
              'Methodology separation — Liao and Brooks-Corey are evaluated in separate inner tabs. Both use the same selected Kr source; Water-Oil uses Krow and Gas-Oil uses its separate Krog endpoint.',
            ],
          },
          {
            type: 'note',
            title: 'Automatic repair',
            text: 'If the recommended combination fails a check at a zone, the app tries swapping the offending parameter to its next-best physically valid candidate (a few times) before marking the combination invalid — and tells you it did so.',
          },
        ],
      },
      {
        id: 'auto-availability',
        title: 'Availability and unavailable curves',
        blocks: [
          {
            type: 'paragraph',
            text: 'Before Run, the source summary shows requested and complete samples for Water-Oil and Gas-Oil. Expand an availability warning to see the exact sample codes and missing values. In Review, each family retains those requested/used/unavailable counts so the provenance of a recommendation stays visible after the report is restored.',
          },
          {
            type: 'bullets',
            items: [
              'A missing family is reported as unavailable; the app does not attempt a regression on an empty set.',
              'A methodology tab is shown only when its Pc family has usable results. If its shared Kr prerequisites are absent, it explains exactly which values are missing instead of labelling the zone valid.',
              'For legacy reports that predate separate Gas-Oil Krog, rendering can use the historic shared endpoint. New runs always preserve distinct Water-Oil Krow and Gas-Oil Krog values.',
            ],
          },
        ],
      },
      {
        id: 'auto-tolerances',
        title: 'Rounding tolerances',
        blocks: [
          {
            type: 'paragraph',
            text: 'Curve points, the derived lambda and the equation coefficients all carry limited precision, so the very last points of a Pc curve can dip a fraction of a milli-psi below zero at the free-water level (where Pc approaches an entry pressure that is itself near zero).',
          },
          {
            type: 'bullets',
            items: [
              'These tiny negatives are numerical noise, not physics — the validators tolerate values inside a small band around zero and the displayed curves snap them to 0.',
              'A genuinely negative Pc, from a bad entry-pressure correlation, is orders of magnitude larger and is still rejected.',
              'Displayed numbers stay rounded for readability; the tolerance only affects whether a near-zero value counts as a violation.',
            ],
          },
        ],
      },
      {
        id: 'auto-flags',
        title: 'Reading the flags',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Reject (✕)',
                description:
                  'A physical hard gate or a failed curve check. The candidate cannot be recommended.',
                effect:
                  'Rejected candidates are greyed out in the override list; tick “Show rejected candidates” to see and, if you insist, force one.',
              },
              {
                name: 'Warning (⚠)',
                description:
                  'The pick is usable but something is worth a look — extrapolation beyond the sampled range, few samples, or a near-bound prediction.',
                effect:
                  'Shown but not disqualifying. Warnings on your staged selection are summarised in the Apply dialog.',
              },
              {
                name: 'Info',
                description:
                  'Context, not a problem — most often low R² (selection driven by shape) or the “selected by convention” tie-break note.',
                effect:
                  'Purely informational; it tells you why a pick looks the way it does.',
              },
            ],
          },
        ],
      },
      {
        id: 'auto-review-actions',
        title: 'Reviewing and applying',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Each parameter shows all four candidates with their score, R² and flags; the Recommended and Best R² columns are marked.',
              'Override any parameter from its dropdown — choosing the recommendation back clears the override; overridden parameters are tagged.',
              'Compare the Recommended / Best R² / Current tabs with their per-zone curve previews to see how the selection plays out before committing.',
              'Apply opens a confirmation summarising your overrides and any warnings, then applies the selection and re-runs the correlation for the chosen sample scope.',
            ],
          },
          {
            type: 'note',
            title: 'It is a recommendation',
            text: 'Auto Correlation encodes a defensible default, not a final answer. Review the suggestions, lean on the flags, and override whenever your geological knowledge of the samples says so.',
          },
        ],
      },
    ],
  },
];

/**
 * Topic list for the Correlation page Help: the shared FZI/RQI and
 * correlation reference (from the Zones help) with the Auto Correlation
 * topics inserted right after the “Correlation” topic, so the
 * correlation-related help reads as one group.
 */
export const correlationHelpTopics: HelpTopic[] = (() => {
  const correlationIndex = zonesHelpTopics.findIndex(
    topic => topic.id === 'zones-correlation'
  );
  if (correlationIndex === -1) {
    return [...zonesHelpTopics, ...autoCorrelationHelpTopics];
  }
  return [
    ...zonesHelpTopics.slice(0, correlationIndex + 1),
    ...autoCorrelationHelpTopics,
    ...zonesHelpTopics.slice(correlationIndex + 1),
  ];
})();
